/**
 * Locations Service – real API only.
 */

import { serviceLogger } from '../utils/serviceLogger';
import { normalizeServiceError } from '../utils/serviceError';
import * as realImpl from './real/locationsService.real';

const wrap = (fnName) => async (...args) => {
  try {
    return await realImpl[fnName](...args);
  } catch (error) {
    serviceLogger.error(`[${fnName}] Error:`, error);
    throw normalizeServiceError(error);
  }
};

export const getLocationsByFacilityPaginated = wrap('getLocationsByFacilityPaginated');
export const getLocationsByFacility = wrap('getLocationsByFacility');
export const getLocation = wrap('getLocation');

export const getLocationsBulkStatus = async (locationIds) => {
  if (!locationIds || locationIds.length === 0) return { success: true, data: [] };
  try {
    return await realImpl.getLocationsBulkStatus(locationIds);
  } catch (error) {
    serviceLogger.error('[getLocationsBulkStatus] Error:', error);
    throw normalizeServiceError(error);
  }
};

export const createLocation = wrap('createLocation');
export const duplicateLocation = wrap('duplicateLocation');
export const updateLocation = wrap('updateLocation');
export const deleteLocation = wrap('deleteLocation');

export const deleteMultipleLocations = async (locationIds) => {
  if (!locationIds || locationIds.length === 0) return;
  try {
    return await realImpl.deleteMultipleLocations(locationIds);
  } catch (error) {
    serviceLogger.error('[deleteMultipleLocations] Error:', error);
    throw normalizeServiceError(error);
  }
};

/**
 * Get locations for multiple facilities (uses getLocationsByFacility with concurrency).
 */
export const getLocationsByMultipleFacilities = async (
  facilityIds,
  params = {},
  concurrency = 3,
) => {
  if (!facilityIds || facilityIds.length === 0) {
    return { success: true, data: {} };
  }
  try {
    const results = {};
    const errors = {};
    for (let i = 0; i < facilityIds.length; i += concurrency) {
      const batch = facilityIds.slice(i, i + concurrency);
      const batchPromises = batch.map(async (facilityId) => {
        try {
          const response = await getLocationsByFacility(facilityId, params);
          return { facilityId, locations: response.data };
        } catch (error) {
          errors[facilityId] = error.message;
          return { facilityId, locations: [] };
        }
      });
      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(({ facilityId, locations }) => {
        results[facilityId] = locations;
      });
    }
    if (Object.keys(errors).length > 0) {
      serviceLogger.warn('[getLocationsByMultipleFacilities] Some facilities failed:', errors);
    }
    return {
      success: true,
      data: results,
      errors: Object.keys(errors).length > 0 ? errors : undefined,
    };
  } catch (error) {
    serviceLogger.error('[getLocationsByMultipleFacilities] Error:', error);
    throw normalizeServiceError(error);
  }
};

/**
 * Batch get locations by IDs (uses getLocation with concurrency).
 */
export const getLocationsBatch = async (locationIds, concurrency = 5) => {
  if (!locationIds || locationIds.length === 0) return [];
  const results = [];
  for (let i = 0; i < locationIds.length; i += concurrency) {
    const batch = locationIds.slice(i, i + concurrency);
    const batchResults = await Promise.all(batch.map((id) => getLocation(id)));
    results.push(...batchResults);
  }
  return results;
};

/**
 * Create location in multiple facilities (uses createLocation with concurrency).
 */
export const createLocationInMultipleFacilities = async (
  facilityIds,
  locationData,
  concurrency = 3,
) => {
  if (!facilityIds || facilityIds.length === 0) return [];
  const results = [];
  const errors = [];
  for (let i = 0; i < facilityIds.length; i += concurrency) {
    const batch = facilityIds.slice(i, i + concurrency);
    const batchPromises = batch.map(async (facilityId) => {
      try {
        return await createLocation(facilityId, locationData);
      } catch (error) {
        errors.push({ facilityId, error: error.message });
        return null;
      }
    });
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.filter((r) => r !== null));
  }
  if (errors.length > 0) {
    serviceLogger.warn('[createLocationInMultipleFacilities] Some locations failed to create:', errors);
  }
  return results;
};
